/**
 * The Supabase client factory. Both apps create exactly one client through this.
 *
 *   web:    createGymClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY)
 *   mobile: createGymClient(url, anonKey, { auth: { storage: secureStorage, ... } })
 *
 * It refuses a service-role key outright. That key bypasses every row-level security
 * policy; it lives only in `supabase/functions/` (CLAUDE.md), and an app that was
 * handed one by mistake must fail loudly at start-up, not quietly see every gym.
 */
import { createClient, type SupabaseClient, type SupabaseClientOptions } from '@supabase/supabase-js';

import type { Database } from './database.types';
import { GymError } from './errors';

export type GymSupabaseClient = SupabaseClient<Database>;

/** The `role` claim of a JWT, without verifying it (verification is the server's job). */
export function jwtRole(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : // Node without atob (very old) — not expected, but never crash on it.
          '';
    const role = (JSON.parse(json) as { role?: unknown }).role;
    return typeof role === 'string' ? role : null;
  } catch {
    return null;
  }
}

export function createGymClient(
  url: string | undefined,
  anonKey: string | undefined,
  options?: SupabaseClientOptions<'public'>,
): GymSupabaseClient {
  if (!url || !anonKey) {
    throw new Error(
      'Supabase URL or anon key is missing. Run `npm run db:up` (writes .env.local) or copy .env.example.',
    );
  }
  if (jwtRole(anonKey) === 'service_role') {
    throw new Error('Refusing to start: this app was given the service-role key. Use the anon key.');
  }
  return createClient<Database>(url, anonKey, options);
}

/**
 * Call an Edge Function and get its JSON back, or a `GymError` carrying the function's
 * own `{ error: { code, message } }`. supabase-js leaves the body of a failed call
 * unread; reading it here means the person sees "A member with mobile number … already
 * exists" rather than "Edge Function returned a non-2xx status code".
 */
export async function callFunction<T>(
  client: GymSupabaseClient,
  name: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await client.functions.invoke<T>(name, body ? { body } : {});
  if (!error) return data as T;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    let payload: unknown = null;
    try {
      payload = await context.clone().json();
    } catch {
      /* not JSON */
    }
    const inner = (payload as { error?: { code?: string; message?: string; fieldErrors?: Record<string, string> } } | null)
      ?.error;
    throw new GymError(inner?.message ?? error.message, {
      code: inner?.code ?? null,
      status: context.status,
      fieldErrors: inner?.fieldErrors ?? {},
    });
  }
  throw error;
}
